import { Controller } from '@hotwired/stimulus';

// The TV page (docs/SCREENS.md § The TV page, § Live, walk/TV.md): the turns,
// the refresh, the wake lock and the fullscreen button. Nobody touches a wall.
//
// Each turn stays its own data-seconds, then the next; after the last, the
// first. The screen's stream pushes a refresh the moment the admin changes
// something; besides it, a morph of this same URL (`replace`, kio's dashboard
// way) every refresh seconds on its own clock, never waiting for a lap, so a
// host without the cable still converges. A refresh keeps the turn on the
// glass (by its id, kept in sessionStorage under the screen); a turn that is
// gone hands its place to the next. Offline the visit fails and the last
// frame stays.
export default class extends Controller {
  static targets = ['turn', 'full'];
  static values = { refresh: { type: Number, default: 60 }, key: String };

  connect() {
    this.at = 0;
    this.shown = this.recall();
    this.wake = () => document.visibilityState === 'visible' && this.awake();
    this.fullness = () => this.fullTargets.forEach((b) => (b.hidden = !!document.fullscreenElement));
    // The morph puts the server's `hidden` back: the same turn stays on the
    // glass, on its own timer; a turn that is gone starts the next one.
    this.morphed = () => (this.turnTargets.some((t) => t.id === this.shown) ? this.show() : this.turn());
    document.addEventListener('visibilitychange', this.wake);
    document.addEventListener('fullscreenchange', this.fullness);
    document.addEventListener('turbo:morph', this.morphed);
    this.clock = setInterval(() => this.redraw(), this.refreshValue * 1000);
    this.awake();
    this.fullness();
    this.turn();
  }

  disconnect() {
    clearTimeout(this.timer);
    clearInterval(this.clock);
    document.removeEventListener('visibilitychange', this.wake);
    document.removeEventListener('fullscreenchange', this.fullness);
    document.removeEventListener('turbo:morph', this.morphed);
    this.lock?.release().catch(() => {});
    this.lock = null;
  }

  // One turn on the glass for its seconds, then the next.
  turn() {
    clearTimeout(this.timer);
    const turn = this.show();
    if (!turn) return;
    this.timer = setTimeout(() => this.next(), (Number(turn.dataset.seconds) || 10) * 1000);
  }

  next() {
    const turns = this.turnTargets;
    if (turns.length === 0) return;
    this.at = (this.at + 1) % turns.length;
    this.shown = turns[this.at].id;
    this.turn();
  }

  // The kept turn where it still is, else whatever took its place.
  show() {
    const turns = this.turnTargets;
    if (turns.length === 0) return null;
    const kept = turns.findIndex((t) => t.id === this.shown);
    this.at = kept >= 0 ? kept : this.at % turns.length;
    const turn = turns[this.at];
    turns.forEach((t) => (t.hidden = t !== turn));
    this.shown = turn.id;
    this.remember(turn.id);
    return turn;
  }

  redraw() {
    window.Turbo?.visit(window.location.href, { action: 'replace' });
  }

  get storeKey() {
    return `fire:tv:${this.keyValue}`;
  }

  recall() {
    try {
      return sessionStorage.getItem(this.storeKey);
    } catch {
      return null;
    }
  }

  remember(id) {
    try {
      sessionStorage.setItem(this.storeKey, id);
    } catch {
      // A TV browser without storage keeps the turn in memory only.
    }
  }

  // Asked again on every return to visible: the browser drops the lock when
  // the page hides. Refused (an old TV, battery saver), the page still runs.
  async awake() {
    try {
      this.lock = await navigator.wakeLock?.request('screen');
    } catch {
      this.lock = null;
    }
  }

  // The browser wants a click for fullscreen, so it is a button.
  full() {
    this.element.requestFullscreen?.().catch(() => {});
  }
}
